import debug from '../utils/console'
import { createUUID } from '#imports'
import { once } from 'lodash-es'
import { OpenFeature, type Client } from '@openfeature/web-sdk'
import {
  COHORTS,
  COHORT_COOKIE,
  SPLIT_TEST_NAME_COOKIE,
  TARGETING_KEY_COOKIE,
  cohortFromTargetingKey,
} from '../openfeature/utils'
import {
  GROUP_COOKIE,
  getTenantGroupKey,
  groupCookieValue,
  isBucketedAttribute,
} from '../openfeature/cacheKey'
import {
  createServerFlagClient,
  type ServerFlagState,
} from '../openfeature/serverFlagClient'
import { hasShiftHostApi } from '../utils/host-browser-apis/hasHostApi'
import { useWindowSize } from './useWindowSize'
import { useLocale } from '../store/LocaleStore'
import { isHydratingServerRender } from '../utils/render/hydration'
import type { GroupPattern } from '../openfeature/ssrAllowList'
import {
  SSR_ALLOW_LIST_FLAG,
  isGroupAllowed,
  parseSsrAllowList,
} from '../openfeature/ssrAllowList'

const logger = debug.withTag('openfeature')

const result = new Map<string, Client>()

const EVALUATIONS = new Set([
  'getBooleanValue',
  'getStringValue',
  'getNumberValue',
  'getObjectValue',
  'getBooleanDetails',
  'getStringDetails',
  'getNumberDetails',
  'getObjectDetails',
])

/**
 * One client for a page the server rendered.
 *
 * While the browser hydrates, every read answers from the server's evaluation
 * in the payload, so Vue reproduces the markup it was given instead of
 * hydrating a different branch. After that, a flag the render read keeps
 * answering from the server for the rest of the page view, and everything else
 * is the browser provider's own.
 *
 * The second part is what keeps a page consistent with itself. Browsers fetch
 * the flag config through a proxy cached for an hour, the server reads it fresh,
 * so for an hour after a flag change the two disagree. Rows the browser adds
 * after hydration, the next feed batch for one, would be built from the other
 * value than the rows above them. One page, one set of answers, and a change
 * reaches the visitor on the next load, which is when the rendered rows would
 * follow it anyway.
 *
 * `rendersFromServer` names the flags that shape the render and that the
 * payload actually carries. The allow list flag is kept out of the payload on
 * purpose, so it stays a browser read.
 *
 * A proxy rather than a hand written wrapper because the whole `Client` surface
 * has to keep working, handlers and hooks included.
 */
export function renderedPageClient(
  server: Client,
  browser: Client,
  rendersFromServer: (flag: string) => boolean
): Client {
  const bound = (source: Client, property: string | symbol) => {
    const value = Reflect.get(source, property, source)
    return typeof value === 'function' ? value.bind(source) : value
  }
  return new Proxy(browser, {
    get(target, property) {
      if (isHydratingServerRender()) return bound(server, property)
      if (typeof property === 'string' && EVALUATIONS.has(property)) {
        return (flag: string, ...rest: unknown[]) => {
          const source = rendersFromServer(flag) ? server : target
          const evaluate = Reflect.get(source, property, source)
          if (typeof evaluate !== 'function') return undefined
          return Reflect.apply(evaluate, source, [flag, ...rest])
        }
      }
      return bound(target, property)
    },
  })
}

let loading: Promise<Client> | null = null

async function initializeOpenFeature(): Promise<{
  client: Client
  browser?: Client
  loaded: Promise<Client>
}> {
  // The server already evaluated every flag for this request with the same
  // context built below (server/middleware/ssr-flags.ts), and the render reads
  // those values through a synchronous client. Without a server evaluation the
  // SDK's no-op client answers with the in code defaults.
  if (import.meta.server) {
    const state = useState<ServerFlagState | null>('ssr-flags').value
    const serverClient = state
      ? createServerFlagClient(state)
      : OpenFeature.getClient()
    return { client: serverClient, loaded: Promise.resolve(serverClient) }
  }
  // The values the server rendered this page from. They stay in charge until
  // hydration finishes, see hydrationClient below.
  const serverState = useState<ServerFlagState | null>('ssr-flags').value
  if (loading) {
    await loading
  }
  if (result.has('client') && result.get('client') !== undefined) {
    const existingClient = result.get('client')
    if (existingClient) {
      return {
        client: existingClient,
        loaded: Promise.resolve(existingClient),
      }
    }
  }
  const maxAge = 1000 * 60 * 60 * 24 * 365 * 10 // 10 years
  const targetingKey = useCookie(TARGETING_KEY_COOKIE, {
    maxAge,
  })
  targetingKey.value = targetingKey.value || createUUID()

  // Assign persistent cohort (0-9) on first visit
  const cohortCookie = useCookie(COHORT_COOKIE, { maxAge })
  if (!cohortCookie.value || !COHORTS.includes(cohortCookie.value)) {
    cohortCookie.value = cohortFromTargetingKey(String(targetingKey.value))
  }

  const filters = useNuxtApp().$filters || {
    userAgent: null,
    isMobile: null,
  }

  const params = useRoute().query
  const rawUtmSource = params.utm_source
  const utmSource = Array.isArray(rawUtmSource) ? rawUtmSource[0] : rawUtmSource
  const isShiftBrowser =
    'isShiftBrowser' in params
      ? params.isShiftBrowser === 'true'
      : hasShiftHostApi()
  const splitTestNameCookie = useCookie(SPLIT_TEST_NAME_COOKIE, {
    maxAge,
  })

  const engagementData = userEngagementStore()

  // The one cookie a server render reads. It names the group this visitor
  // belongs to rather than the visitor, so the CDN holds one rendered page per
  // group instead of one per person. A tenant that does not enforce a group key
  // is not server rendered and writes nothing.
  const groupKey = getTenantGroupKey(useAppConfig().tenant ?? '')
  const groupCookie = useCookie(GROUP_COOKIE, { maxAge })
  // Immediate: the openfeature plugin dependsOn user-segment, which awaits
  // loadUserEngagement, so the store is already resolved by the time this
  // registers and a change would never come.
  // Width is in the group because the hero ad placement and the card column
  // count both branch on it, and the two hero placements reserve different
  // boxes. Watched so a resize rewrites the cookie for the next load.
  const { width } = useWindowSize()
  // Read from the same store the feed request reads, so the token names the
  // language and country the page was actually built with.
  const localeStore = useLocale()
  // Which groups render, read from the same flag the server gate reads once
  // the provider is up. A group that would not render writes no cookie, so it
  // never becomes a cache key at the edge and never reaches the origin. The
  // server still checks, so a stale cookie only ever costs one shell response.
  const ssrAllowList = ref<GroupPattern[]>([])
  watch(
    () => [
      engagementData.daysSinceInstall,
      engagementData.installDate,
      width.value,
      localeStore.locale,
      localeStore.country,
      ssrAllowList.value,
    ],
    () => {
      if (!groupKey?.enforce) return
      const daysSinceInstall = engagementData.daysSinceInstall
      const installDate = Number(engagementData.installDate)
      // A bucket index the browser guessed at is a page rendered for the wrong
      // group. Without both numbers the visitor keeps the client shell, which
      // is what every visitor gets today, and an older cookie goes with it so
      // the next request cannot be rendered for a group this visitor may have
      // left.
      const bucketed = groupKey.attributes.filter(isBucketedAttribute)
      const known =
        (!bucketed.includes('daysSinceInstall') ||
          typeof daysSinceInstall === 'number') &&
        (!bucketed.includes('installDate') || Number.isFinite(installDate))
      if (!known) {
        groupCookie.value = null
        return
      }
      // Null when the locale is off the closed list. Null again when the
      // group is not on the allow list. Both remove the cookie and keep this
      // visitor on the client shell.
      const value = groupCookieValue({
        tenant: groupKey.tenant,
        cohort: String(cohortCookie.value ?? ''),
        isMobile: Boolean(filters.isMobile),
        isShiftBrowser,
        daysSinceInstall: daysSinceInstall ?? undefined,
        installDate,
        viewport: width.value,
        locale: `${localeStore.locale}-${localeStore.country ?? 'US'}`,
      })
      groupCookie.value =
        value && isGroupAllowed(groupKey.tenant, value, ssrAllowList.value)
          ? value
          : null
    },
    { immediate: true }
  )

  // Update user context docs - https://redbrickmedia.atlassian.net/wiki/x/LwC63Q
  OpenFeature.setContext({
    ...filters, // CC mapping = custom.isMobile, custom.userAgent
    ...params, // CC mapping = query parameters (eg. siteSettings, enableGamesCategory, etc.)
    targetingKey: targetingKey.value, // CC mapping = identifier
    ...(splitTestNameCookie.value // CC mapping = custom.splittestname
      ? { splittestname: splitTestNameCookie.value }
      : {}),
    utmSource: utmSource || null, // CC mapping = custom.utm_source
    isEngaged: engagementData.isEngaged, // CC mapping = custom.isEngaged (plugin updates once loaded from localStorage)
    daysSinceInstall: engagementData.daysSinceInstall, // CC mapping = custom.daysSinceInstall
    installDate: Number(engagementData.installDate), // CC mapping = custom.installDate
    cohort: cohortCookie.value, // CC mapping = custom.cohort
    isShiftBrowser, // CC mapping = custom.isShiftBrowser
  })

  logger.log({ filters })

  const {
    registerMultipleFeatureFlagProviders,
    registerFeatureFlagProvider,
    teardown,
  } = await import('../openfeature/client.web')

  if (loading) {
    await loading
  }

  const { configCatSdkKey } = useRuntimeConfig().public
  const {
    openfeature = {
      multiProvider: false,
      localProvider: false,
      queryProvider: false,
    },
  } = useAppConfig()
  const {
    multiProvider = false,
    localProvider = false,
    queryProvider = false,
  } = openfeature

  const options = {
    configCatKey: configCatSdkKey ?? undefined,
    localProvider,
    queryProvider,
  }
  const openfeatureLoaded = multiProvider
    ? registerMultipleFeatureFlagProviders(options)
    : registerFeatureFlagProvider(options)

  loading = openfeatureLoaded
  const client = await openfeatureLoaded
  logger.log({ client, OpenFeature, context: OpenFeature.getContext() })
  let cleanupRegistered = false

  const splitTestName = client.getStringValue('splittestname', '')
  if (splitTestName) splitTestNameCookie.value = splitTestName
  if (groupKey?.enforce) {
    ssrAllowList.value = parseSsrAllowList(
      groupKey.tenant,
      client.getStringValue(SSR_ALLOW_LIST_FLAG, '')
    )
  }

  OpenFeature.setContext({
    ...OpenFeature.getContext(),
    splittestname: splitTestNameCookie.value ?? '',
    cohort: cohortCookie.value ?? '',
    isShiftBrowser,
  })

  if (typeof window !== 'undefined') {
    if (!cleanupRegistered) {
      window.addEventListener('beforeunload', () => {
        logger.log('Teardown OpenFeature client on beforeunload')
        // Use sync teardown or fire-and-forget async
        teardown().catch((err) => logger.error('Teardown failed:', err))
      })
      cleanupRegistered = true
    }

    // Report cohort as ntp_cohort property to host browser analytics
    try {
      setProfileProperty('ntp_cohort', String(cohortCookie.value || ''))
    } catch (err) {
      logger.warn('Failed to set NTP cohort property for analytics', err)
    }
  }

  // Only a page the server rendered has anything to hydrate against. A client
  // shell has no server evaluation and gets the browser client unchanged.
  const renderFlags = new Set(groupKey?.renderFlags ?? [])
  const rendered = serverState
    ? renderedPageClient(
        createServerFlagClient(serverState),
        client,
        (flag) =>
          renderFlags.has(flag) && serverState.values[flag] !== undefined
      )
    : client

  return {
    client: rendered,
    browser: client,
    loaded: openfeatureLoaded.then(() => rendered),
  }
}

// One shared initialisation in the browser. On the server every request has its
// own evaluation, so the result must not be remembered across requests.
export const useOpenFeature: () => Promise<{
  client: Client
  /** The browser provider's own client, for reads that must not be answered from the render. */
  browser?: Client
  loaded: Promise<Client>
}> = import.meta.server ? initializeOpenFeature : once(initializeOpenFeature)
